import { useEffect, useState, type ReactElement } from 'react';
import { api, type AccountView, type InvitationView, type User } from '../api';
import { Sheet } from './Sheet';

/**
 * Who has an account, and what they may do.
 *
 * Until now an administrator could create projects and nothing else: a
 * colleague could only be given access by somebody running SQL. This is the
 * screen that was missing. Invitations by mail come later; for now an admin
 * sets a password and passes it on, which is honest about what it is.
 */
export function UsersSheet({ me, onClose }: { me: User; onClose: () => void }): ReactElement {
  const [users, setUsers] = useState<AccountView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [invitations, setInvitations] = useState<InvitationView[]>([]);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'member' as 'admin' | 'member' });

  const load = async () => {
    try {
      setUsers((await api.accounts()).users);
      setInvitations((await api.invitations()).invitations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Die Konten konnten nicht geladen werden.');
    }
  };
  useEffect(() => {
    void load();
  }, []);

  /** Every change goes through here so one failure cannot leave a stale list. */
  const act = async (what: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await what();
      await load();
      setNote(done);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Das hat nicht geklappt.');
    } finally {
      setBusy(false);
    }
  };

  const invite = async (event: React.FormEvent) => {
    event.preventDefault();
    await act(
      () => api.invite({ email: form.email.trim(), name: form.name.trim(), role: form.role }),
      `Einladung an ${form.email.trim()} verschickt. Der Link gilt eine Woche.`,
    );
    setForm({ name: '', email: '', password: '', role: 'member' });
    setInviting(false);
  };

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    await act(
      () => api.createAccount({ ...form, email: form.email.trim(), name: form.name.trim() }),
      `Zugang für ${form.name.trim() || form.email.trim()} angelegt. Das Passwort weitergeben — es steht nirgends sonst.`,
    );
    setForm({ name: '', email: '', password: '', role: 'member' });
    setAdding(false);
  };

  const resetPassword = async (user: AccountView) => {
    const password = prompt(`Neues Passwort für ${user.name} (mindestens 10 Zeichen):`);
    if (!password) return;
    await act(() => api.setAccountPassword(user.id, password), `Passwort für ${user.name} gesetzt.`);
  };

  return (
    <Sheet title="Konten" onClose={onClose}>
      {error ? <p className="error-text">{error}</p> : null}
      {note ? <p className="hint" style={{ color: 'var(--ok-text)' }}>{note}</p> : null}

      {adding ? (
        <form className="card" style={{ margin: '0 0 14px' }} onSubmit={create}>
          <label className="field">
            <span>Name</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </label>
          <label className="field">
            <span>E-Mail</span>
            <input
              type="email"
              inputMode="email"
              autoCapitalize="none"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </label>
          <label className="field">
            <span>Passwort</span>
            <input
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              minLength={10}
              required
            />
            <p className="hint">Mindestens zehn Zeichen. Ohne Mailversand gibst du es selbst weiter.</p>
          </label>
          <label className="field">
            <span>Rolle</span>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'member' })}
            >
              <option value="member">Mitarbeiterin — eigene Projekte</option>
              <option value="admin">Admin — darf zusätzlich Konten verwalten</option>
            </select>
          </label>
          <div className="stack">
            <button className="primary" type="submit" disabled={busy}>
              Zugang anlegen
            </button>
            <button type="button" className="ghost" onClick={() => setAdding(false)}>
              Abbrechen
            </button>
          </div>
        </form>
      ) : inviting ? (
        <form className="card" style={{ margin: '0 0 14px' }} onSubmit={invite}>
          <label className="field">
            <span>E-Mail</span>
            <input
              type="email"
              inputMode="email"
              autoCapitalize="none"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </label>
          <label className="field">
            <span>Name (optional)</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <p className="hint">Nur für die Anrede in der Mail.</p>
          </label>
          <label className="field">
            <span>Rolle</span>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'member' })}
            >
              <option value="member">Mitarbeiterin — eigene Projekte</option>
              <option value="admin">Admin — darf zusätzlich Konten verwalten</option>
            </select>
          </label>
          <div className="stack">
            <button className="primary" type="submit" disabled={busy}>
              Einladung verschicken
            </button>
            <button type="button" className="ghost" onClick={() => setInviting(false)}>
              Abbrechen
            </button>
          </div>
        </form>
      ) : (
        <div className="stack" style={{ marginBottom: 14 }}>
          <button className="primary" onClick={() => setInviting(true)}>
            Per E-Mail einladen
          </button>
          <button onClick={() => setAdding(true)}>Zugang mit Passwort anlegen</button>
        </div>
      )}

      {invitations.length > 0 ? (
        <>
          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 8px' }}>Eingeladen, noch nicht angenommen</h3>
          <div className="list" style={{ marginBottom: 14 }}>
            {invitations.map((invitation) => (
              <div className="row" key={invitation.id}>
                <div className="grow">
                  <div className="title" style={{ wordBreak: 'break-all' }}>{invitation.email}</div>
                  <div className="sub">
                    gültig bis {new Date(invitation.expires_at).toLocaleDateString('de-DE')}
                    {invitation.role === 'admin' ? ' · als Admin' : ''}
                  </div>
                </div>
                <button
                  className="small"
                  disabled={busy}
                  onClick={() => void act(() => api.revokeInvitation(invitation.id), 'Einladung zurückgezogen.')}
                >
                  Zurückziehen
                </button>
              </div>
            ))}
          </div>
        </>
      ) : null}

      <div className="list">
        {users.map((user) => (
          <div className="row" key={user.id} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
            <div className="spread">
              <div className="grow">
                <div className="title">
                  {user.name}
                  {user.id === me.userId ? ' · du' : ''}
                </div>
                <div className="sub" style={{ wordBreak: 'break-all' }}>{user.email}</div>
                <div className="sub">
                  {user.role === 'admin' ? <span className="badge">Admin</span> : <span className="badge">Mitarbeiterin</span>}
                  {user.disabled ? <span className="badge err"> gesperrt</span> : null}
                  {user.last_login_at
                    ? ` · zuletzt ${new Date(user.last_login_at).toLocaleDateString('de-DE')}`
                    : ' · noch nie angemeldet'}
                </div>
              </div>
            </div>
            {/* The own account has no buttons at all: every one of them would
                be a way to lock yourself out of your own installation. */}
            {user.id === me.userId ? (
              <div className="sub">Das eigene Konto lässt sich hier nicht ändern.</div>
            ) : (
              <div className="chips">
                <button
                  className="chip"
                  disabled={busy}
                  onClick={() =>
                    void act(
                      () => api.setAccountRole(user.id, user.role === 'admin' ? 'member' : 'admin'),
                      user.role === 'admin' ? `${user.name} ist jetzt Mitarbeiterin.` : `${user.name} ist jetzt Admin.`,
                    )
                  }
                >
                  {user.role === 'admin' ? 'Zu Mitarbeiterin' : 'Zu Admin'}
                </button>
                <button className="chip" disabled={busy} onClick={() => void resetPassword(user)}>
                  Passwort setzen
                </button>
                <button
                  className="chip"
                  disabled={busy}
                  onClick={() =>
                    void act(
                      () => api.setAccountDisabled(user.id, !user.disabled),
                      user.disabled ? `${user.name} ist wieder freigeschaltet.` : `${user.name} ist gesperrt.`,
                    )
                  }
                >
                  {user.disabled ? 'Freischalten' : 'Sperren'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <p className="hint" style={{ marginTop: 16 }}>
        Sperren wirkt sofort und auf allen Geräten: die laufenden Anmeldungen werden ungültig. Gelöscht wird nichts —
        Projekte einer gesperrten Person bleiben stehen.
      </p>
    </Sheet>
  );
}
